'use client'

import { useState, useContext, useEffect } from 'react'
import { richTextRenderFactory } from '@lib/rich-text-options'
import type { TypeExpandableContentFields } from '@lib/generated-types'
import slugify from '@sindresorhus/slugify'
import useAnalytics, { type SPContext } from '@hooks/use-analytics'
import {
  BlockContext,
  ItemContext,
  PageContext,
  PlaceContext,
  ProviderContext,
} from '@lib/analytics'
import useUrlHash from '@hooks/use-url-hash'
import type { ExpandableContentProps as SCLExpandableContentProps } from '@shc/ui'
import { Collapsible, ExpandableContent as SCLExpandableContent } from '@shc/ui'

type ExpandableContentProps = {
  offsetQuerySelectors?: string[]
} & TypeExpandableContentFields &
  SCLExpandableContentProps

const ExpandableContent = ({
  sectionLink,
  items,
  offsetQuerySelectors,
}: ExpandableContentProps) => {
  const [combinedHeaderHeight, setCombinedHeaderHeight] = useState(0)
  const [expand, setExpand] = useState(new Array(items.length).fill(false))
  const { trackSnowplow } = useAnalytics()
  const hash = useUrlHash()
  // Environment contexts
  const pageContext = useContext(PageContext)
  const placeContext = useContext(PlaceContext)
  const providerContext = useContext(ProviderContext)
  const blockContext = useContext(BlockContext)
  const itemContext = useContext(ItemContext)
  const environmentContexts: SPContext[] = [
    pageContext,
    placeContext,
    providerContext,
    blockContext,
    itemContext,
  ].filter((context) => context !== null)

  const onClick = (idx: number) => {
    const newOpen = expand.concat()
    newOpen[idx] = !expand[idx]
    setExpand(newOpen)
  }

  useEffect(() => {
    const calcHeaderHeight = offsetQuerySelectors?.reduce((acc, selector) => {
      const element = document.querySelector<HTMLElement>(selector)
      return acc + (element?.offsetHeight ?? 0)
    }, 0)

    setCombinedHeaderHeight(calcHeaderHeight ?? 0)
  }, [offsetQuerySelectors])

  useEffect(() => {
    if (hash) {
      for (const [index, item] of items.entries()) {
        if (item.fields.sectionLink && hash === slugify(item.fields.sectionLink)) {
          const newOpen = expand.concat()
          newOpen[index] = true
          setExpand(newOpen)
          return
        }
      }
    }
     
  }, [hash])

  return (
    <SCLExpandableContent id={sectionLink && slugify(sectionLink)} className="mt-8">
      {items?.map(({ fields, sys }, idx) => (
        <Collapsible
          id={fields.sectionLink && slugify(fields.sectionLink)}
          key={sys.id + fields.title + idx}
          style={{ scrollMarginTop: `${combinedHeaderHeight}px` }}
          heading={fields.title}
          description={richTextRenderFactory()(fields.description)}
          onOpenChange={(isOpen) => {
            trackSnowplow({
              event: { name: isOpen ? 'component_expand' : 'component_collapse', data: {} },
              contexts: [
                { name: 'component', data: { component_text: fields.title } },
                ...environmentContexts,
              ],
            })
          }}
          open={expand[idx]}
          onClick={() => onClick(idx)}
        />
      ))}
    </SCLExpandableContent>
  )
}

export default ExpandableContent
